import { ConfirmationDialog } from '@/components/ConfirmationDialog';

/**
 * BulkConfirmationDialog — Safety gate before destructive bulk actions.
 *
 * Required for ALL bulk endpoint calls (>20 manual items OR 'all' mode).
 * Per-item path (≤20) does NOT require confirmation (existing behavior).
 *
 * Shows:
 * - Action name
 * - "all matching" or item count
 * - Resource type
 * - "This action will be processed in the background and cannot be undone."
 * - [Cancel] [Confirm] buttons
 *
 * The irreversible Praetorian hard-delete variant is opted into EXPLICITLY via
 * the `isHardDelete` prop (never sniffed from the action name). It swaps in
 * stronger, audit-trail-aware copy — a "Permanently delete …?" title, a
 * "Delete Permanently" confirm label, and a "cannot be undone and no audit
 * trail is kept" description — with an optional entity-specific `hardDeleteHint`
 * appended (e.g. a "use Reject/Delete instead" steer). Every other action keeps
 * the original background-processed copy byte-for-byte.
 *
 * @example
 * ```tsx
 * <BulkConfirmationDialog
 *   isOpen={showConfirm}
 *   actionName="freeze"
 *   resourceLabel="assets"
 *   isSelectAll={selectionState.mode === 'all'}
 *   itemCount={selectedRows.length}
 *   onCancel={() => setShowConfirm(false)}
 *   onConfirm={() => { submitBulk(payload); setShowConfirm(false); }}
 *   isPending={isPending}
 * />
 * ```
 *
 * @example Hard-delete variant
 * ```tsx
 * <BulkConfirmationDialog
 *   isOpen={deleteConfirm.confirmPayload !== null}
 *   actionName="delete permanently"
 *   resourceLabel="vulnerabilities"
 *   isHardDelete
 *   hardDeleteHint="To close findings with a reason and preserve the audit trail, use Reject instead."
 *   isSelectAll={false}
 *   itemCount={selectedCount}
 *   onCancel={deleteConfirm.closeConfirmation}
 *   onConfirm={deleteConfirm.submitConfirmed}
 * />
 * ```
 */
export interface BulkConfirmationDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Human-readable action name (e.g. "freeze", "delete", "export") */
  actionName: string;
  /** Resource label in plural (e.g. "assets", "vulnerabilities") */
  resourceLabel: string;
  /** Whether selection is in 'all' mode (shows "all matching" instead of count) */
  isSelectAll?: boolean;
  /** Number of items to act on (used when isSelectAll is false) */
  itemCount?: number;
  /** Called when user clicks Cancel */
  onCancel: () => void;
  /** Called when user clicks Confirm */
  onConfirm: () => void;
  /** Whether the bulk action is currently submitting */
  isPending?: boolean;
  /**
   * Marks the irreversible Praetorian hard-delete variant. When true, the
   * dialog uses stronger copy: a "Permanently delete …?" title, a
   * "Delete Permanently" confirm label, and an audit-trail warning in the
   * description. Explicit opt-in — never inferred from the action name.
   */
  isHardDelete?: boolean;
  /**
   * Entity-specific guidance appended to the hard-delete description (e.g. a
   * "use Reject instead" / "use Delete instead" steer). Only rendered when
   * `isHardDelete` is true.
   */
  hardDeleteHint?: string;
  /**
   * Optional eligibility note appended to the (non-hard-delete) description,
   * e.g. "3 of 10 already approved and will be unaffected." Rendered only for
   * the standard variant; ignored when isHardDelete is true.
   */
  eligibilityNote?: string;
  /** Test identifier for the dialog root element */
  'data-testid'?: string;
}

/**
 * BulkConfirmationDialog — Confirmation dialog for bulk actions.
 */
export function BulkConfirmationDialog({
  isOpen,
  actionName,
  resourceLabel,
  isSelectAll = false,
  itemCount,
  onCancel,
  onConfirm,
  isPending = false,
  isHardDelete = false,
  hardDeleteHint,
  eligibilityNote,
  'data-testid': dataTestId = 'bulk-confirmation-dialog',
}: BulkConfirmationDialogProps) {
  const selectionDescription = isSelectAll
    ? `all matching ${resourceLabel}`
    : `${itemCount?.toLocaleString() ?? 'selected'} ${resourceLabel}`;

  const description = isHardDelete
    ? `You are about to permanently delete ${selectionDescription}. This cannot be undone and no audit trail is kept.${
        hardDeleteHint ? ` ${hardDeleteHint}` : ''
      }`
    : `You are about to ${actionName} ${selectionDescription}. This action will be processed in the background and cannot be undone.${
        eligibilityNote ? ` ${eligibilityNote}` : ''
      }`;

  const title = isHardDelete
    ? `Permanently delete ${selectionDescription}?`
    : `Confirm bulk ${actionName}`;

  return (
    <ConfirmationDialog
      open={isOpen}
      onClose={onCancel}
      onConfirm={onConfirm}
      title={title}
      description={description}
      confirmLabel={isHardDelete ? 'Delete Permanently' : 'Confirm'}
      variant="destructive"
      isLoading={isPending}
      loadingLabel="Submitting..."
      data-testid={dataTestId}
    />
  );
}
